const CSS_PX_PER_MM = 96 / 25.4;
const CSS_PX_PER_PT = 96 / 72;

let measureCanvas = null;

const number = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function getEffectiveTextWidthMm(element, layoutWidthMm) {
  const x = Math.max(0, number(element?.x_mm, 0));
  const layoutWidth = Math.max(1, number(layoutWidthMm, 60));
  const available = Math.max(0.5, layoutWidth - x);
  const configured = Math.max(0.5, number(element?.w_mm, 20));
  if ((element?.fit_mode || 'shrink') === 'expand') return available;
  return Math.min(configured, available);
}

function fallbackMeasure(text, fontSizePt, bold) {
  const px = fontSizePt * CSS_PX_PER_PT;
  let units = 0;
  for (const ch of String(text || '')) {
    if (/\s/.test(ch)) units += 0.32;
    else if (/[ilI1.,:;|'`]/.test(ch)) units += 0.30;
    else if (/[MW@#%&]/.test(ch)) units += 0.92;
    else if (/[A-Z]/.test(ch)) units += 0.68;
    else units += 0.54;
  }
  return units * px * (bold ? 1.06 : 1);
}

function measureTextPx(text, fontSizePt, bold) {
  if (typeof document === 'undefined') return fallbackMeasure(text, fontSizePt, bold);
  try {
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    if (!ctx) return fallbackMeasure(text, fontSizePt, bold);
    ctx.font = `${bold ? 700 : 400} ${fontSizePt * CSS_PX_PER_PT}px Arial, Helvetica, sans-serif`;
    return ctx.measureText(String(text || '')).width;
  } catch (_) {
    return fallbackMeasure(text, fontSizePt, bold);
  }
}

export function getTextRenderMetrics(element, text, layoutWidthMm) {
  const widthMm = getEffectiveTextWidthMm(element, layoutWidthMm);
  const configuredPt = Math.max(0.75, number(element?.font_size, 6));
  const heightMm = Math.max(0.5, number(element?.h_mm, 3));
  const maxByHeightPt = heightMm * (72 / 25.4) * 0.82;
  const basePt = Math.min(configuredPt, Math.max(0.75, maxByHeightPt));
  const content = String(text || '');
  if (!content) return { widthMm, fontSizePt: basePt };

  const availablePx = Math.max(1, widthMm * CSS_PX_PER_MM - 2);
  const measuredPx = measureTextPx(content, basePt, !!element?.bold);
  if (!measuredPx || measuredPx <= availablePx) return { widthMm, fontSizePt: basePt };

  // A small safety margin keeps browser/PDF/Zebra rounding differences from clipping the last glyph.
  const fitted = basePt * (availablePx / measuredPx) * 0.92;
  return { widthMm, fontSizePt: Math.max(0.75, Math.min(basePt, fitted)) };
}
